import { useQuery, QueryClient } from "@tanstack/react-query";
import { GET_MY_USER_RECORDS } from "../api/queries";
import { useMemo } from "react";
import { graphQLClient } from "../graphqlClient";
import { useStores } from "./useStores";
import {
  FullUserRecord,
  ExtendedUser,
  UniqueIdentifier,
  FacetTotals,
} from "@/types/user";

const getPreferredUserRecord = (
  userRecords: FullUserRecord[],
  preferredUserPath: string | null,
): FullUserRecord => {
  const defaultUserRecord = userRecords[0];
  if (!preferredUserPath) {
    return defaultUserRecord;
  }
  return (
    userRecords.find((record) => record.path === preferredUserPath) ||
    defaultUserRecord
  );
};

// Returns a full list of user records and loading state
export const useFullCurrentUser = () => {
  const { userStore } = useStores();
  const preferredUserPath = userStore?.getPreferredUserPath();

  const {
    data: userRecords,
    isLoading: userRecordsLoading,
    error: userRecordsError,
  } = useQuery<FullUserRecord[]>({
    queryKey: ["myUserRecords"],
    queryFn: async () => {
      const response = await graphQLClient.request<{
        getMyUserRecords: FullUserRecord[];
      }>(GET_MY_USER_RECORDS);
      return response.getMyUserRecords;
    },

    staleTime: 1000 * 10, // Consider data fresh for 10 seconds
    gcTime: 1000 * 60 * 60 * 12, // Keep unused data in cache for 12 hours
  });

  const activeUserRecord = useMemo((): ExtendedUser | null => {
    // Get the first user record
    if (userRecordsLoading) {
      return null;
    }

    if (!userRecords || userRecords.length === 0) {
      return null;
    }
    const userRecord = getPreferredUserRecord(userRecords, preferredUserPath);

    // Get user group from localStorage like the login page does
    const storedUserGroups = localStorage.getItem("userGroups");
    const userGroups = storedUserGroups ? JSON.parse(storedUserGroups) : [];
    const userGroup =
      userGroups.length > 0 ? userGroups[0].toLowerCase() : "unknown";

    const uniqueIdentifiers: UniqueIdentifier[] = userRecords.map((record) => {
      const pathParts = record.path?.split("#") || [];

      // Extract school ID and section ID from path parts
      const districtID =
        pathParts.find((part) => part.startsWith("dis-")) || null;
      const schoolID =
        pathParts.find((part) => part.startsWith("sch-")) || null;
      const sectionID =
        pathParts.find((part) => part.startsWith("sec-")) || null;
      return {
        userPath: record.path || "",
        schoolID,
        sectionID,
        districtID,
      };
    });

    return {
      ...userRecord,
      userID: userRecord.id,
      role: userGroup,
      schoolLevel: null, // TODO - find where we use this and use feature flags instead
      userPath: userRecord.path || "",
      uniqueIdentifiers,
    };
  }, [userRecords, preferredUserPath]);

  return {
    userRecords,
    userRecordsLoading,
    userRecordsError,
    activeUserRecord,
  };
};

export const updateUserRecordCache = (
  queryClient: QueryClient,
  updateFunction: (record: FullUserRecord) => FullUserRecord,
): FullUserRecord[] => {
  const userRecords = queryClient.getQueryData<FullUserRecord[]>([
    "myUserRecords",
  ]);
  if (!userRecords) return [];

  const updatedUserRecords = userRecords.map(updateFunction);
  queryClient.setQueryData(["myUserRecords"], updatedUserRecords);
  return updatedUserRecords;
};

// Returns the one active user record
export const useCurrentUser = (): ExtendedUser | Record<string, never> => {
  const { activeUserRecord } = useFullCurrentUser();
  return activeUserRecord || ({} as const);
};

// Calculate total scores for each facet type across ladders returns an object with the facet type
// as the key and the total score and possible score as the value.
// Example:
// {
//   "RIASEC:R": { total: 10, possible: 10 },
//   "RIASEC:I": { total: 10, possible: 10 },
//   "RIASEC:A": { total: 10, possible: 10 },
//   "RIASEC:S": { total: 10, possible: 10 },
export const getFacetTotals = (
  currentUser: ExtendedUser | Record<string, never>,
): FacetTotals => {
  if (!currentUser?.facets) {
    return {};
  }

  return currentUser.facets.reduce((totals: FacetTotals, ladder) => {
    ladder.facets?.forEach(({ facet, score }) => {
      if (facet && score && score.length >= 2) {
        if (!totals[facet]) {
          totals[facet] = { total: 0, possible: 0 };
        }
        totals[facet].total += score[0];
        totals[facet].possible += score[1];
      }
    });
    return totals;
  }, {});
};
